import { FC, HTMLAttributes, useCallback, useMemo, useState } from "react";
import {
    Button,
    Col,
    Collapse,
    Form,
    Input,
    Popover,
    Row,
    Space,
    Spin,
    Tabs,
} from "../../../design-system";
import { clsx } from "clsx";

import { FeedbackValue, AnalysisFeedback, DealAnalysisOutput } from "../../../types/ProjectTypes";
import { saveAnalysisFeedback, removeAnalysisFeedback } from "../../../common/api/ProjectClient";
import { useUserState } from "../../../common/UserContext";
import { splitHtmlByH2 } from "../../../common/Html";
import { Sanitized } from "../../../common/components/Sanitized";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { handleError } from "../../../common/ErrorHandling";
import styles from "../../orderform/detail-v2/components/MultiSectionHtmlSummary.module.less";
import feedbackStyles from "../../orderform/detail-v2/components/Feedback.module.less";

const sections = [
    {
        key: "contractSummary",
        title: "Contract summary",
        description: "Here's a summary of your contract.",
        field: "contractSummary" as const,
        useTabs: true,
    },
    {
        key: "negotiationStrategy",
        title: "Negotiation strategy",
        description: "Potential cost-savings strategy checklist.",
        field: "negotiationStrategy" as const,
        useTabs: false,
    },
    {
        key: "topLegalPriorities",
        title: "Top legal priorities",
        description: "Critical legal issues and strategies that demand immediate attention.",
        field: "topLegalPriorities" as const,
        useTabs: false,
    },
];

// -- Subsection feedback thumbs up/down widget --

interface SubsectionFeedbackWidgetProps extends HTMLAttributes<HTMLDivElement> {
    value?: FeedbackValue;
    onFeedbackAdd?: (value: FeedbackValue, comment?: string) => Promise<void>;
    onFeedbackRemove?: () => Promise<void>;
}

type FormValues = { content: string };

const SubsectionFeedbackWidget: FC<SubsectionFeedbackWidgetProps> = ({
    value,
    onFeedbackAdd,
    onFeedbackRemove,
    className,
    ...props
}) => {
    const [form] = Form.useForm<FormValues>();
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleThumbsUp = async () => {
        setLoading(true);
        try {
            if (value === "good") {
                await onFeedbackRemove?.();
            } else {
                if (value === "bad") {
                    await onFeedbackRemove?.();
                }
                await onFeedbackAdd?.("good");
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleThumbsDown = async () => {
        setLoading(true);
        try {
            if (value === "bad") {
                await onFeedbackRemove?.();
            } else {
                setShowConfirm(true);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (formValues: FormValues) => {
        setLoading(true);
        try {
            if (value === "good") {
                await onFeedbackRemove?.();
            }
            await onFeedbackAdd?.("bad", formValues.content);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const closeAndResetPopover = () => {
        setShowConfirm(false);
        form.resetFields();
    };

    const popoverContent =
        value === "bad" ? (
            <>
                <Row>
                    <Col flex={1}></Col>
                    <Col>
                        <SvgIcon
                            name="close"
                            size={12}
                            color="#1890FF"
                            className={feedbackStyles.close}
                            onClick={closeAndResetPopover}
                        />
                    </Col>
                </Row>
                <div className={feedbackStyles.title}>Thank you</div>
                <div className={feedbackStyles.subtitle}>Your feedback has been received.</div>
                <div className={feedbackStyles.footer}>
                    <Button type="primary" onClick={closeAndResetPopover}>
                        Got it!
                    </Button>
                </div>
            </>
        ) : (
            <>
                <Row>
                    <Col flex={1} className={feedbackStyles.header}>
                        Tell us why...
                    </Col>
                    <Col>
                        <SvgIcon
                            name="close"
                            size={12}
                            color="#1890FF"
                            onClick={() => setShowConfirm(false)}
                            className={feedbackStyles.close}
                        />
                    </Col>
                </Row>
                <Form form={form} onFinish={handleSubmit} size="small" disabled={loading}>
                    <Form.Item
                        name={["content"]}
                        rules={[
                            { required: true, message: "Please enter your feedback" },
                            { type: "string", max: 10_000, message: "Maximum 10,000 characters" },
                        ]}
                    >
                        <Input.TextArea
                            bordered={false}
                            rows={3}
                            className={feedbackStyles.input}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Row justify="end">
                            <Space size={16}>
                                <Button
                                    onClick={() => setShowConfirm(false)}
                                    className={feedbackStyles.cancel}
                                >
                                    It's ok
                                </Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Submit
                                </Button>
                            </Space>
                        </Row>
                    </Form.Item>
                </Form>
            </>
        );

    return (
        <div {...props} className={clsx(feedbackStyles.container, className)}>
            <Spin spinning={loading} delay={250}>
                <SvgIcon
                    name="thumbsup"
                    size={18}
                    className={clsx(feedbackStyles.like, value === "good" && feedbackStyles.active)}
                    onClick={handleThumbsUp}
                />
                <Popover
                    content={<div className={feedbackStyles.content}>{popoverContent}</div>}
                    trigger={["click"]}
                    open={showConfirm}
                    showArrow={false}
                    onOpenChange={(open) => {
                        if (!open) {
                            setShowConfirm(open);
                            if (value === "bad") form.resetFields();
                        }
                    }}
                >
                    <SvgIcon
                        name="thumbsdown"
                        size={18}
                        className={clsx(
                            feedbackStyles.dislike,
                            value === "bad" && feedbackStyles.active
                        )}
                        onClick={handleThumbsDown}
                    />
                </Popover>
            </Spin>
        </div>
    );
};

// -- Main report view --

interface AnalysisReportProps {
    projectId?: number;
    analysisVersion?: number;
    output?: DealAnalysisOutput;
    userFeedback?: Record<string, AnalysisFeedback[]>;
}

export const AnalysisReport: FC<AnalysisReportProps> = ({
    projectId,
    analysisVersion,
    output,
    userFeedback: initialFeedback,
}) => {
    const { me } = useUserState();
    const userId = (me as any)?.id as number | undefined;
    const report = output?.report;
    const [feedback, setFeedback] = useState(initialFeedback);

    const subsectionsBySection = useMemo(() => {
        if (!report) return {};
        return Object.fromEntries(
            sections.map((s) => [s.key, splitHtmlByH2(report[s.field] ?? "")])
        );
    }, [report]);

    const [activeKeys, setActiveKeys] = useState<string[]>(sections.map((s) => s.key));

    const getUserFeedbackValue = useCallback(
        (sectionIdentifier: string): FeedbackValue | undefined => {
            if (!feedback || userId == null) return undefined;
            const key = `report.${sectionIdentifier}`;
            const entries = feedback[key];
            if (!entries) return undefined;
            return entries.find((e) => e.userId === userId)?.value;
        },
        [feedback, userId]
    );

    const handleFeedbackAdd = useCallback(
        async (subsectionId: string, value: FeedbackValue, comment?: string) => {
            if (projectId == null || analysisVersion == null) return;
            try {
                const updated = await saveAnalysisFeedback(projectId, analysisVersion, {
                    path: `report.${subsectionId}`,
                    value,
                    comment,
                });
                setFeedback(updated.userFeedback);
            } catch (error) {
                handleError(error);
            }
        },
        [projectId, analysisVersion]
    );

    const handleFeedbackRemove = useCallback(
        async (subsectionId: string) => {
            if (projectId == null || analysisVersion == null) return;
            try {
                const updated = await removeAnalysisFeedback(
                    projectId,
                    analysisVersion,
                    `report.${subsectionId}`
                );
                setFeedback(updated.userFeedback);
            } catch (error) {
                handleError(error);
            }
        },
        [projectId, analysisVersion]
    );

    return (
        <Space direction="vertical" size="large" fullWidth>
            {sections.map((section) => {
                const subsections = subsectionsBySection[section.key];
                if (!subsections || subsections.length === 0) return null;

                const useTabMode = section.useTabs && subsections.length > 1;

                const content = useTabMode ? (
                    <Tabs
                        items={subsections.map((sub, index) => {
                            const subsectionId = `${section.key}.${index}`;
                            return {
                                label: <Sanitized className={styles.tabTitle} content={sub.key} />,
                                key: index.toString(),
                                children: (
                                    <div
                                        className={clsx(
                                            styles.section,
                                            `il-summary-wrapper il-summary-wrapper--${section.key}`
                                        )}
                                    >
                                        <Sanitized className="il-summary" content={sub.value} />
                                        <SubsectionFeedbackWidget
                                            className={styles.feedback}
                                            value={getUserFeedbackValue(subsectionId)}
                                            onFeedbackAdd={(val, comment) =>
                                                handleFeedbackAdd(subsectionId, val, comment)
                                            }
                                            onFeedbackRemove={() =>
                                                handleFeedbackRemove(subsectionId)
                                            }
                                        />
                                    </div>
                                ),
                            };
                        })}
                    />
                ) : (
                    <>
                        {subsections.map((sub, index) => {
                            const subsectionId = `${section.key}.${index}`;
                            return (
                                <div
                                    className={clsx(
                                        styles.section,
                                        `il-summary-wrapper il-summary-wrapper--${section.key}`
                                    )}
                                    key={index}
                                >
                                    <Sanitized className="il-summary" content={sub.value} />
                                    <SubsectionFeedbackWidget
                                        className={styles.feedback}
                                        value={getUserFeedbackValue(subsectionId)}
                                        onFeedbackAdd={(val, comment) =>
                                            handleFeedbackAdd(subsectionId, val, comment)
                                        }
                                        onFeedbackRemove={() => handleFeedbackRemove(subsectionId)}
                                    />
                                </div>
                            );
                        })}
                    </>
                );

                return (
                    <Collapse
                        key={section.key}
                        activeKey={activeKeys}
                        onChange={(value) => setActiveKeys(value as string[])}
                    >
                        <Collapse.Panel
                            title={section.title}
                            description={section.description}
                            key={section.key}
                        >
                            {content}
                        </Collapse.Panel>
                    </Collapse>
                );
            })}
        </Space>
    );
};
